import { FilterNode } from '@/dashboards/SpendSummary/SpendSummary';
import { FormattedSpendSummaryData } from '@/dashboards/SpendSummary/types';
import { RoiCalculate } from '@/dashboards/SpendSummary/utils';

/** One row of the Spend Summary table. `variable` is absent for category rows. */
export type SpendSummaryRow = {
    category: string;
    variable?: string;
    contribution: number;
    spend: number;
    contributionPercentageTotal: number;
    roi: number;
};

export type SpendSummaryRows = {
    data: SpendSummaryRow[];
    /**
     * ROI over the rows the filter tree left visible — the table's footer value.
     * Distinct from the Total Media ROI KPI, which is computed over the whole
     * period (see `useTotalMediaRoi`).
     */
    totalRoiFiltered: number;
};

type BuildArgs = {
    spendSummaryData: FormattedSpendSummaryData;
    selectedFilterNodes: { [key: string]: FilterNode };
    calculate: RoiCalculate;
};

/**
 * Rows sort by their category's total contribution (descending), so variables
 * stay grouped under their category, then by the row's own contribution.
 */
const compareByCategoryThenContribution =
    (spendSummaryData: FormattedSpendSummaryData) =>
    (a: SpendSummaryRow, b: SpendSummaryRow) => {
        const totalContributionA = spendSummaryData.totalContributionByCategory.get(
            a.category,
        )!;
        const totalContributionB = spendSummaryData.totalContributionByCategory.get(
            b.category,
        )!;
        if (totalContributionA < totalContributionB) {
            return 1;
        }
        if (totalContributionA > totalContributionB) {
            return -1;
        }

        return b.contribution - a.contribution;
    };

/** One row per selected category. */
export const buildCategoryRows = ({
    spendSummaryData,
    selectedFilterNodes,
    calculate,
}: BuildArgs): SpendSummaryRows => {
    let totalContribution = 0;
    let totalSpend = 0;
    let totalContributionProduct = 0;

    const data = spendSummaryData.categoryData
        .filter(({ category }) => !!selectedFilterNodes[category])
        .map(({ category }) => {
            const contribution =
                spendSummaryData.totalContributionByCategory.get(category)!;
            const spend = spendSummaryData.totalSpendByCategory.get(category)!;
            const contributionProduct =
                spendSummaryData.totalContributionProductByCategory.get(
                    category,
                )!;

            totalContribution += contribution;
            totalSpend += spend;
            totalContributionProduct += contributionProduct;

            return {
                category,
                contribution,
                spend,
                contributionPercentageTotal:
                    (contribution / spendSummaryData.totalContribution) * 100,
                roi: calculate({
                    spend,
                    contribution,
                    contributionCostProduct: contributionProduct,
                }),
            };
        })
        .sort(compareByCategoryThenContribution(spendSummaryData));

    return {
        data,
        totalRoiFiltered: calculate({
            spend: totalSpend,
            contribution: totalContribution,
            contributionCostProduct: totalContributionProduct,
        }),
    };
};

/**
 * One row per selected variable. A variable is selected either directly or by
 * its whole category being selected.
 *
 * Note the running totals are accumulated *before* empty variables are dropped,
 * so `totalRoiFiltered` includes variables that never reach the table. That is
 * pre-existing behaviour, preserved deliberately.
 */
export const buildVariableRows = ({
    spendSummaryData,
    selectedFilterNodes,
    calculate,
}: BuildArgs): SpendSummaryRows => {
    let totalContribution = 0;
    let totalSpend = 0;
    let totalContributionProduct = 0;

    const data = spendSummaryData.variableData
        .filter(
            ({ category, displayName }) =>
                !!selectedFilterNodes[category] ||
                !!selectedFilterNodes[displayName],
        )
        .map(({ category, displayName }) => {
            const contribution =
                spendSummaryData.totalContributionByVariable.get(displayName)!;
            const spend =
                spendSummaryData.totalSpendByVariable.get(displayName)!;
            const contributionProduct =
                spendSummaryData.totalContributionProductByVariable.get(
                    displayName,
                )!;

            totalContribution += contribution;
            totalSpend += spend;
            totalContributionProduct += contributionProduct;

            return {
                category,
                variable: displayName,
                contribution,
                spend,
                contributionPercentageTotal:
                    (contribution / spendSummaryData.totalContribution) * 100,
                roi: calculate({
                    spend,
                    contribution,
                    contributionCostProduct: contributionProduct,
                }),
            };
        })
        .filter(({ contribution, spend }) => contribution + spend > 0)
        .sort(compareByCategoryThenContribution(spendSummaryData));

    return {
        data,
        totalRoiFiltered: calculate({
            spend: totalSpend,
            contribution: totalContribution,
            contributionCostProduct: totalContributionProduct,
        }),
    };
};

export const buildSpendSummaryRows = ({
    modelBreakdown,
    ...args
}: BuildArgs & { modelBreakdown: 'category' | 'variable' }): SpendSummaryRows =>
    modelBreakdown === 'category'
        ? buildCategoryRows(args)
        : buildVariableRows(args);
